/* eslint-disable no-console */
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import {
  Input,
  AutoSaveField as Field,
  SelectAutocomplete,
  Checkbox
} from "../../../../components/Form";
import {
  OTHER_OPTION_CODE,
  BASE_PATH,
  MAX_STREET_NUMBER_LENGTH,
  MAX_PO_BOX_NUMBER_LENGTH,
  MAX_OTHER_FIELD_LENGTH,
  MAX_OFFICE_NUMBER_LENGTH
} from "./constants";
import { Label } from "../CompanyDetails/styled";

export const CompanyPreferredMailingAddressComponent = ({
  organisationAddressLine2,
  organisationAddressLine1,
  organisationPoBox,
  organisationEmirateCity,
  organisationCountry,
  premisesType,
  otherPremisesType,
  values,
  setValues,
  setFieldValue,
  setFieldTouched
}) => {
  useEffect(() => {
    setFieldValue("addressLine1", organisationAddressLine1);
    setFieldValue("addressLine2", organisationAddressLine2);
    setFieldValue("poBox", organisationPoBox);
    setFieldValue("typeOfSpaceOccupied.spaceType", premisesType);
    setFieldTouched("typeOfSpaceOccupied.spaceType", false);
    setFieldValue("typeOfSpaceOccupied.others", otherPremisesType);
    setFieldTouched("typeOfSpaceOccupied.others", false);
    setFieldValue("emirateCity", organisationEmirateCity);
    setFieldTouched("emirateCity", false);
    setFieldValue("country", organisationCountry);
    setFieldTouched("country", false);
  }, [
    organisationAddressLine1,
    organisationAddressLine2,
    organisationPoBox,
    organisationEmirateCity,
    organisationCountry,
    premisesType,
    otherPremisesType,
    setFieldValue,
    setFieldTouched
  ]);

  return (
    <>
      <Grid container spacing={3} sx={{ mt: 0, rowGap: "16px" }}>
        <Grid item sm={12} xs={12}>
          <Label>Mailing</Label>
        </Grid>
        <Grid item sm={12} xs={12}>
          <Field
            name="sameAsCompanyAddress"
            path="prospect.orgKYCDetails.sameAsCompanyAddress"
            label="Same as company address"
            component={Checkbox}
            onSelect={() => {
              setValues({
                sameAsCompanyAddress: true,
                addressLine2: values.sameAsCompanyAddress ? organisationAddressLine2 : "",
                addressLine1: values.sameAsCompanyAddress ? organisationAddressLine1 : "",
                emirateCity: values.sameAsCompanyAddress ? organisationEmirateCity : "",
                poBox: values.sameAsCompanyAddress ? organisationPoBox : "",
                country: values.sameAsCompanyAddress ? organisationCountry : "",
                typeOfSpaceOccupied: {
                  spaceType: values.sameAsCompanyAddress ? premisesType : "",
                  others: values.sameAsCompanyAddress ? otherPremisesType : ""
                }
              });
            }}
            inputProps={{ tabIndex: 0 }}
            disabled
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item sm={6} xs={12}>
          <Field
            name="addressLine1"
            path={`${BASE_PATH}.addressLine1`}
            label="Office / Shop Number"
            placeholder="Office / Shop Number"
            contextualHelpText="Give the Registered Address of the company as given in Trade license or the operating or head office address of the company. This will be used as primary contact and Cheque book will be delivered to this address."
            InputProps={{
              inputProps: { maxLength: MAX_OFFICE_NUMBER_LENGTH, tabIndex: 1 }
            }}
            value={organisationAddressLine1}
            component={Input}
            disabled
          />
        </Grid>
        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            mt: {
              xs: 2,
              sm: 0
            }
          }}
        >
          <Field
            name="typeOfSpaceOccupied.spaceType"
            path={`${BASE_PATH}.typeOfSpaceOccupied.spaceType`}
            datalistId="spaceType"
            label="Type of Space Occupied"
            isSearchable
            component={SelectAutocomplete}
            tabIndex="0"
            value={premisesType}
            disabled
          />
        </Grid>

        {values.typeOfSpaceOccupied.spaceType === OTHER_OPTION_CODE && (
          <Grid
            item
            sm={12}
            xs={12}
            sx={{
              mt: {
                xs: 2,
                sm: 0
              }
            }}
          >
            <Field
              name="typeOfSpaceOccupied.others"
              path={`${BASE_PATH}.typeOfSpaceOccupied.others`}
              label="Other(Specify)"
              placeholder="Other(Specify)"
              component={Input}
              InputProps={{
                inputProps: { maxLength: MAX_OTHER_FIELD_LENGTH, tabIndex: 2 }
              }}
              disabled
            />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item sm={6} xs={12}>
          <Field
            name="addressLine2"
            path={`${BASE_PATH}.addressLine2`}
            label="Street / Location"
            placeholder="Street / Location"
            component={Input}
            InputProps={{
              inputProps: { maxLength: MAX_STREET_NUMBER_LENGTH, tabIndex: 2 }
            }}
            value={organisationAddressLine2}
            disabled
          />
        </Grid>
        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            mt: {
              xs: 2,
              sm: 0
            }
          }}
        >
          <Field
            name="poBox"
            path={`${BASE_PATH}.poBox`}
            label="PO Box Number"
            placeholder="AB1234"
            component={Input}
            InputProps={{
              inputProps: {
                maxLength: MAX_PO_BOX_NUMBER_LENGTH,
                tabIndex: values.typeOfSpaceOccupied.spaceType === OTHER_OPTION_CODE ? 3 : 2
              }
            }}
            value={organisationPoBox}
            disabled
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item sm={6} xs={12}>
          <Field
            name="emirateCity"
            path={`${BASE_PATH}.emirateCity`}
            datalistId="emirateCity"
            label="Emirate / City"
            isSearchable
            component={SelectAutocomplete}
            tabIndex="0"
            value={organisationEmirateCity}
            disabled
          />
        </Grid>
        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            mt: {
              xs: 3,
              sm: 0
            }
          }}
        >
          <Field
            name="country"
            disabled
            label="Country"
            path={`${BASE_PATH}.country`}
            component={SelectAutocomplete}
            datalistId="country"
          />
        </Grid>
      </Grid>
    </>
  );
};

CompanyPreferredMailingAddressComponent.propTypes = {
  organisationAddressLine2: PropTypes.string,
  organisationAddressLine1: PropTypes.string,
  organisationPoBox: PropTypes.string,
  organisationEmirateCity: PropTypes.string,
  organisationCountry: PropTypes.string,
  premisesType: PropTypes.string,
  otherPremisesType: PropTypes.string,
  values: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired
};
